import React, { useState, useRef } from 'react';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiX, FiFileText, FiTrash2 } from 'react-icons/fi';

const getApiBaseUrl = () =>
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_REST_API_ENDPOINT?.replace(/\/$/, '');

const ImportRoutesModal = ({ isOpen, toggle, onSuccess, token }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    if (isUploading) return;
    setSelectedFile(null);
    setIsDragging(false);
    toggle();
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const hasValidExt = validExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidExt) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV (.csv) file.');
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size should not exceed 10 MB.');
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      const apiBaseUrl = getApiBaseUrl();
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${apiBaseUrl}/delivery-rate/v2/routes/import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const message =
        response?.data?.message || 'Delivery routes imported successfully.';

      if (response?.data?.error) {
        toast.error(message);
      } else {
        toast.success(message);
        if (onSuccess) onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Error importing routes:', error);
      toast.error(error?.response?.data?.message || 'Failed to import routes');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const fileUrl = `${process.env.PUBLIC_URL || ''}/Sample File.xlsx`;
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('File not found');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Sample File.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading sample file:', error);
      const link = document.createElement('a');
      link.href = `${process.env.PUBLIC_URL || ''}/Sample%20File.xlsx`;
      link.setAttribute('download', 'Sample File.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / (k ** i)).toFixed(1))} ${sizes[i]}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      style={{ maxWidth: '620px', width: '100%' }}
    >
      <ModalBody
        style={{
          padding: 0,
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <h5
            style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            Import File
          </h5>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#111827';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Modal Body / Dropzone */}
        <div style={{ padding: '20px' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleFileSelect(e.target.files[0]);
              }
              e.target.value = '';
            }}
          />

          <div
            role="button"
            tabIndex={0}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              if (!selectedFile) fileInputRef.current?.click();
            }}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !selectedFile) {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            style={{
              border: isDragging ? '1.5px dashed #2563eb' : '1.5px dashed #babfc3',
              borderRadius: '8px',
              backgroundColor: isDragging ? '#eff6ff' : '#fafbfb',
              minHeight: '160px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '30px 20px',
              cursor: selectedFile ? 'default' : 'pointer',
              transition: 'border-color 0.2s, background-color 0.2s',
            }}
          >
            {selectedFile ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: '#ffffff',
                  padding: '12px 18px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  maxWidth: '100%',
                }}
              >
                <FiFileText size={28} color="#2563eb" />
                <div style={{ minWidth: 0, textAlign: 'left' }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#111827',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '320px',
                    }}
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    {formatFileSize(selectedFile.size)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  title="Remove file"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '4px',
                    marginLeft: '8px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #babfc3',
                    borderRadius: '6px',
                    padding: '8px 18px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#202223',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                    transition: 'background-color 0.15s, border-color 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f6f6f7';
                    e.currentTarget.style.borderColor = '#8c9196';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#babfc3';
                  }}
                >
                  + Add file
                </button>
                <span
                  style={{
                    fontSize: '12px',
                    color: '#8c9196',
                    marginTop: '10px',
                  }}
                >
                  or drag and drop your Excel file here
                </span>
              </>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 20px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
          }}
        >
          {/* Download sample link */}
          <button
            type="button"
            onClick={handleDownloadSample}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: '14px',
              color: '#2563eb',
              cursor: 'pointer',
              textDecoration: 'none',
              fontWeight: 500,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            Download sample file
          </button>

          {/* Action Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={isUploading}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #babfc3',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#202223',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => {
                if (!isUploading) e.currentTarget.style.backgroundColor = '#f6f6f7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              style={{
                backgroundColor: !selectedFile || isUploading ? '#e1e3e5' : '#1d4ed8',
                color: !selectedFile || isUploading ? '#8c9196' : '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 18px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: !selectedFile || isUploading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => {
                if (selectedFile && !isUploading) {
                  e.currentTarget.style.backgroundColor = '#1e40af';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedFile && !isUploading) {
                  e.currentTarget.style.backgroundColor = '#1d4ed8';
                }
              }}
            >
              {isUploading ? (
                <>
                  <Spinner size="sm" />
                  <span>Uploading...</span>
                </>
              ) : (
                'Upload and preview'
              )}
            </button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ImportRoutesModal;
